import { Link } from 'react-router-dom';
import { PageWrapper } from '../../shell/PageWrapper.js';
import {
  type LucideIcon,
  BarChart3,
  Building2,
  Inbox,
  Search,
  Send,
  Settings,
  Upload,
} from 'lucide-react';
import { useDashboardKpis } from '../../api/kpis.js';

interface Props {
  tenantId: string;
  tenantName: string;
}

// REQ-FE-029b — Per-tenant landing pane: eyebrow + title + subtitle + KPI
// 4-card row + 7-card navigation grid. Mockup: `02-tenant-aif-overview.png`.
//
// REQ-FE-030 — Pass-3 visual fidelity: swap the emoji nav-card icons for
// Lucide line icons (the emoji rendered as filled glyphs with a salmon
// tint on macOS that fought the mockup's clean-line aesthetic), add the
// "Ouvrir →" coral CTA on the accent card, and render KPI counts that
// were already fetched even while the contract refresh is in flight so
// callers don't see "Chargement…" forever when the KPIs endpoint errors.
//
// The TenantKpis contract surface only exposes openAp / overdue /
// weeklyIntake / topSuppliers + (REQ-BE-029-be-2) disputed / approvedLast30d.
// The subtitle uses `overdue.count` for both "urgentes" and "en retard"
// since the mockup distinguishes them but our model collapses them.
export function TenantOverviewScreen({ tenantId, tenantName }: Props) {
  const kpis = useDashboardKpis(tenantId);

  const aTraiter = kpis.data?.openAp.count ?? null;
  const urgentes = kpis.data?.overdue.count ?? null;
  const enRetard = urgentes; // single field today, both lines surface same value
  const enLitige = kpis.data?.disputed?.count ?? null;
  const approuvees = kpis.data?.approvedLast30d?.count ?? null;
  const slug = tenantName.toUpperCase().replace(/\s+/g, '-');
  // Subtitle resolution: only "Chargement…" while the first response is
  // pending. Once react-query settles (success OR error), drop the
  // spinner so callers always see something useful — partial data, the
  // headline counts at zero, or an explicit error chip.
  const headerHasData = aTraiter !== null && urgentes !== null && enRetard !== null;

  return (
    <PageWrapper>
      <section className="tenant-overview" aria-label={`Vue d'ensemble · ${tenantName}`}>
        <header className="tenant-overview__head">
          <div className="eyebrow" data-testid="tenant-overview-eyebrow">
            {slug}
          </div>
          <h1 className="display tenant-overview__title">
            {tenantName}
            <span className="tenant-overview__title-accent" aria-hidden="true">
              _
            </span>
          </h1>
          <p className="muted tenant-overview__subtitle" data-testid="tenant-overview-subtitle">
            {kpis.isLoading && !headerHasData ? (
              <span>Chargement des indicateurs…</span>
            ) : kpis.isError && !headerHasData ? (
              <span data-testid="tenant-overview-subtitle-error">
                Indicateurs indisponibles pour le moment.
              </span>
            ) : (
              <>
                <strong className="text-coral">{urgentes ?? 0} urgentes</strong>
                {' · '}
                <strong className="text-coral">{enRetard ?? 0} en retard</strong>
                {` sur ${aTraiter ?? 0} à traiter.`}
              </>
            )}
          </p>
        </header>

        <section className="tenant-overview__kpis" data-testid="tenant-overview-kpis">
          <KpiCard label="À traiter" value={aTraiter} tone="neutral" />
          <KpiCard label="Urgentes" value={urgentes} tone="warning" />
          <KpiCard label="En litige" value={enLitige} tone="warning" />
          <KpiCard label="Approuvées (30 J)" value={approuvees} tone="success" />
        </section>

        <section className="tenant-overview__nav-grid" data-testid="tenant-overview-nav">
          <NavCard
            to={`/t/${tenantId}/invoices`}
            Icon={Inbox}
            title="Boîte de réception"
            subtitle="Liste des factures reçues via Iopole, classées par échéance."
            cta="Ouvrir"
            accent
            testId="overview-nav-inbox"
          />
          <NavCard
            to={`/t/${tenantId}/upload`}
            Icon={Upload}
            title="Importer une facture"
            subtitle="Téléverser une facture hors PDP (Factur-X / UBL / CII)."
            testId="overview-nav-upload"
          />
          <NavCard
            to={`/t/${tenantId}/stats`}
            Icon={BarChart3}
            title="Statistiques"
            subtitle="Volume, mix par statut, top fournisseurs, délais de paiement."
            testId="overview-nav-stats"
          />
          <NavCard
            to={`/t/${tenantId}/invoices/new`}
            Icon={Send}
            title="Émettre une facture"
            subtitle="Outbound Factur-X via Iopole — bientôt (S2)."
            testId="overview-nav-send"
          />
          <NavCard
            to={`/t/${tenantId}/annuaire`}
            Icon={Building2}
            title="Fournisseurs"
            subtitle="Annuaire des fournisseurs ayant émis une facture pour cette société."
            testId="overview-nav-suppliers"
          />
          <NavCard
            to={`/t/${tenantId}/invoices?focus=search`}
            Icon={Search}
            title="Rechercher"
            subtitle="Recherche libre dans toutes les factures de cette société — tous statuts."
            testId="overview-nav-search"
          />
          <NavCard
            to={`/t/${tenantId}/settings`}
            Icon={Settings}
            title="Paramètres"
            subtitle="Comptable par défaut, cadence des rappels de validation. Admin uniquement."
            testId="overview-nav-settings"
          />
        </section>
      </section>
    </PageWrapper>
  );
}

function slugifyLabel(label: string): string {
  // Strip accents (NFD then remove combining marks) so the test-id key
  // stays ASCII-only. "À traiter" → "a-traiter", "Approuvées (30 J)" →
  // "approuvees-30-j".
  return label
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function KpiCard({
  label,
  value,
  tone,
  placeholder = false,
}: {
  label: string;
  value: number | null;
  tone: 'neutral' | 'warning' | 'success';
  placeholder?: boolean;
}) {
  return (
    <article
      className={`tenant-overview__kpi tenant-overview__kpi--${tone}`}
      data-testid={`tenant-overview-kpi-${slugifyLabel(label)}`}
    >
      <div className="eyebrow tenant-overview__kpi-label">{label}</div>
      <div className="tenant-overview__kpi-value">
        {placeholder || value === null ? <span className="muted">—</span> : value}
      </div>
      {placeholder ? <div className="muted tenant-overview__kpi-hint">données à venir</div> : null}
    </article>
  );
}

function NavCard({
  to,
  Icon,
  title,
  subtitle,
  accent = false,
  cta,
  testId,
}: {
  to: string;
  Icon: LucideIcon;
  title: string;
  subtitle: string;
  accent?: boolean;
  cta?: string;
  testId: string;
}) {
  return (
    <Link
      to={to}
      className={`tenant-overview__nav-card${accent ? ' tenant-overview__nav-card--accent' : ''}`}
      data-testid={testId}
    >
      <div className="tenant-overview__nav-head">
        <Icon
          className="tenant-overview__nav-icon"
          aria-hidden="true"
          size={16}
          strokeWidth={1.5}
        />
        <span className="tenant-overview__nav-title">{title}</span>
      </div>
      <div className="muted tenant-overview__nav-sub">{subtitle}</div>
      {cta ? (
        <span className="tenant-overview__nav-cta" aria-hidden="true">
          {cta} →
        </span>
      ) : null}
    </Link>
  );
}
